import React, { useEffect, useState, useContext, useRef } from 'react'
import styled, { keyframes } from 'styled-components'
import ModalTest from '../../../BaseModal'
import { CloseDiv, ContainerOfModal, Header, HeaderTitle, InputDiv, Label, Input, ButtonContainer, Button } from '../../../styles/modal-style'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { X, ChevronLeft, ChevronRight, Paperclip, Trash2, Loader2 } from 'lucide-react'
import SelectSupplierInResource from './SelectSupplier'
import InfiniteSelect from '../../../InfiniteSelect'
import ColorField from './ColorField'
import PriceManager, { PriceItem, PriceManagerRef } from '../../../PriceManager'
import QuantityManager, { QuantityItem, QuantityManagerRef } from '../../../QuantityManager'
import ApiService from '../../../../services/ApiService'
import AttachmentApiService from '../../../../services/AttachmentApiService'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import StockContext from '../../../../contexts/StockContext'
import ConfirmationModal from '../../../ModalConfirmation'
import { useTenant } from '../../../../contexts/TenantContext'
import fileIcon from '../../../../assets/addStockModal/pages/file.svg'
import cancelIcon from '../../../../assets/addStockModal/pages/cancel.svg'
import PantoneSelect, { PantoneValue } from '../../../PantoneSelect'
import AttachmentSourceModal from '../../../AttachmentSourceModal'
import AttachmentGalleryModal, { GalleryFile } from '../../../AttachmentGalleryModal'
import openSignedAttachment from '../../../../utils/openSignedAttachment'
import { useNcmTextoPendente, MENSAGEM_NCM_INEXISTENTE } from '../../../../hooks/useNcmTextoPendente'
import NcmErroCampo from '../../../NcmErroCampo'

const spinAnimation = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 40vh;
  gap: 1.2rem;
  width: 100%;
`

const LoadingSpinner = styled(Loader2)`
  animation: ${spinAnimation} 1s linear infinite;
  color: #0090D3;
`

const LoadingText = styled.span`
  font-size: 0.95rem;
  font-weight: 500;
  color: #64748b;
  font-family: 'Inter', sans-serif;
`

const ProgressBarDiv = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0.5rem 0 1.5rem 0;
  width: 100%;
`

const ProgressBarCircle = styled.div<{ $active?: boolean }>`
  width: 1vw;
  height: 1vw;
  border-radius: 2vw;
  background-color: ${({ $active }) => ($active ? '#0090D3' : '#C4C4C4')};
  flex-shrink: 0;
`

const ProgressBarLine = styled.div<{ $active?: boolean }>`
  flex: 1;
  height: 0.5vh;
  background-color: ${({ $active }) => ($active ? '#0090D3' : '#C4C4C4')};
`

const StepGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 1rem;
`

const FileBlock = styled.div`
  display: flex;
  height: 25vh;
  justify-content: space-between;
  margin-top: 0.5rem;
`

const FileBlockDiv = styled.div`
  width: 100%;
`

const FileHeaderText = styled.p`
  font-weight: 700;
  font-size: 0.9vw;
  font-family: 'Inter', sans-serif;
  margin-bottom: 0;
`

const FileInputDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 1vh;
  border: dashed #33A6DC 2px;
  border-radius: 0.5vw;
  background-color: #E6F4FB;
  height: 16vh;
`

const FileInputLabel = styled.label`
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 1vw;
  padding: 1vh 1.5vw;
  background-color: #fff;
  color: #0090D3;
  border: 1px solid #0090D3;
  border-radius: 1.5vw;
  cursor: pointer;

  &.down {
    position: relative;
    top: 1.5vh;
  }
`

const FileInputHidden = styled.input`
  display: none;
`

const FileFirstText = styled.p`
  font-size: 0.9vw;
  margin-top: 1.5vh;
  margin-bottom: 0;
  font-family: 'Inter', sans-serif;
`

const FileSecondText = styled.p`
  color: #696969;
  font-size: 0.8vw;
  margin-top: 0.5vh;
  margin-bottom: 0;
  font-family: 'Inter', sans-serif;
`

const FilesInfoDiv = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 1vh;
  overflow-y: auto;
  max-height: 14vh;
`

const FileInfoDiv = styled.div`
  display: flex;
  align-items: center;
  margin-top: 0.5vh;
  padding: 0.5vh 0.8vw;
  border: 1px solid #D0D0D0;
  border-radius: 0.5vw;
  gap: 0.5vw;
`

const FileItemName = styled.p`
  margin: 0;
  font-size: 0.85vw;
  font-family: 'Inter', sans-serif;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const FileRemoveBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #E53E3E;
  display: flex;
  align-items: center;
  padding: 0;
  flex-shrink: 0;
`

const FileInfoImg = styled.img`
  width: 1.6vw;
  margin-left: 0.2vw;
  flex-shrink: 0;
`

const FileMiddleDiv = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 0.6vh;
  margin-left: 0.4vw;
  flex: 1;
  min-width: 0;
`

const FileUpperMiddleDiv = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`

const FileName = styled.p`
  margin-top: 0;
  margin-bottom: 0;
  font-size: 0.8vw;
  font-family: 'Inter', sans-serif;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
`

const FileProgressContainer = styled.div`
  margin-top: 0.8vh;
  width: 100%;
  height: 4px;
  background-color: #696969;
  border-radius: 2px;
`

const FileProgressFill = styled.div`
  height: 4px;
  background-color: #6ec1e4;
  border-radius: 2px;
  width: 100%;
`

const FileInfoCancelImg = styled.img`
  margin-left: 0.4vw;
  width: 1.3vw;
  cursor: pointer;
  flex-shrink: 0;
`

interface CompanyItemType {
  id: number
  companyName: string
  cnpj: string
}

interface CategoryItemType {
  id: number
  name: string
}

interface TypeItemType {
  id: number
  name: string
}

interface LookupItemType {
  id: number | string
  name: string
  pantone?: PantoneValue | null
}

interface ResourceFormType {
  name: string
  code: string
  category: string
  type: string
  description: string
  prices: PriceItem[]
  quantities?: QuantityItem[]
  totalWeight: string
  lengthInMm: string
  unitQuantity: string
  minQuantity: string
  maxQuantity: string
  stockUnit: string
  dimension1: string
  dimension2: string
  markup?: string
  carrierCode?: string
}

const CreateResourceModal = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const id = searchParams.get('id')
  const sourceId = searchParams.get('sourceId')
  const variation = searchParams.get('variation') === 'true'
  const returnTo = searchParams.get('returnTo')
  const redirectParam = searchParams.get('redirect')
  const { setFeedback } = useContext(FeedbackContext)
  const { refresh: stockContextRefresh, setRefresh: setStockContextRefresh } = useContext(StockContext)
  const { isFashionTenant } = useTenant()
  const redirectPath = returnTo || redirectParam || (isFashionTenant ? '/panel/stock/resource' : '/panel/resource')
  const priceManagerRef = useRef<PriceManagerRef>(null)
  const quantityManagerRef = useRef<QuantityManagerRef>(null)
  const currentPricesRef = useRef<PriceItem[]>([])

  const stepParam = searchParams.get('step')
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(() => {
    if (stepParam === '3') return 3
    if (stepParam === '2') return 2
    return 1
  })
  const [invoiceFiles, setInvoiceFiles] = useState<File[]>([])
  const ncmTexto = useNcmTextoPendente()
  const [externalInvoiceUrl, setExternalInvoiceUrl] = useState<string | null>(null)
  const [isSourceChoiceOpen, setIsSourceChoiceOpen] = useState(false)
  const [isGalleryOpen, setIsGalleryOpen] = useState(false)

  const handleSelectInvoiceFromGallery = async (galleryFiles: GalleryFile[]) => {
    if (!galleryFiles || !galleryFiles.length) return
    const gf = galleryFiles[0]
    if (!gf?.fileUrl) return
    setExternalInvoiceUrl(gf.fileUrl)
    try {
      const signedUrl = gf.signedUrl || (await AttachmentApiService.getSignedUrl(gf.fileUrl))
      const res = await fetch(signedUrl)
      const blob = await res.blob()
      const file = new File([blob], gf.originalName || gf.fileName || gf.title || 'nota-fiscal.pdf', {
        type: gf.mimeType || 'application/pdf',
      })
      setInvoiceFiles([file])
    } catch {
      const fallbackFile = new File([''], gf.originalName || gf.fileName || gf.title || 'nota-fiscal.pdf', {
        type: gf.mimeType || 'application/pdf',
      })
      setInvoiceFiles([fallbackFile])
    }
  }

  const [isValidationModalOpen, setIsValidationModalOpen] = useState(false)
  const [validationFields, setValidationFields] = useState<string[]>([])

  const [selectedColor, setSelectedColor] = useState<LookupItemType | null>(null)
  const [stockUnitTouched, setStockUnitTouched] = useState(true)
  const invoiceInputRef = useRef<HTMLInputElement>(null)
  const [originalColorName, setOriginalColorName] = useState<string | null>(null)
  const [pendingColorPantone, setPendingColorPantone] = useState<PantoneValue | null>(null)
  const [colorError, setColorError] = useState(false)
  const [pantoneError, setPantoneError] = useState(false)
  const [nameError, setNameError] = useState(false)
  const [categoryError, setCategoryError] = useState(false)
  const [materialError, setMaterialError] = useState(false)
  const [selectedSuppliers, setSelectedSuppliers] = useState<CompanyItemType[]>([])
  const [selectedCategories, setSelectedCategories] = useState<CategoryItemType[]>([])
  const [selectedTypes, setSelectedTypes] = useState<TypeItemType[]>([])
  const [selectedMaterial, setSelectedMaterial] = useState<LookupItemType | null>(null)
  const [selectedFormat, setSelectedFormat] = useState<LookupItemType | null>(null)
  const [selectedFinishing, setSelectedFinishing] = useState<LookupItemType | null>(null)
  const [selectedNcm, setSelectedNcm] = useState<LookupItemType | null>(null)
  const [selectedShelf, setSelectedShelf] = useState<LookupItemType | null>(null)
  const [selectedStockLocation, setSelectedStockLocation] = useState<LookupItemType | null>(null)
  const [selectedStockStreet, setSelectedStockStreet] = useState<LookupItemType | null>(null)
  const [previousCategoryId, setPreviousCategoryId] = useState<number | null>(null)
  const [codeError, setCodeError] = useState(false)
  const [availableUnitsList, setAvailableUnitsList] = useState<Array<{ id: number, name: string }>>([])
  const [isLoadingData, setIsLoadingData] = useState<boolean>(() => Boolean(searchParams.get('id') || searchParams.get('sourceId')))
  const [formData, setFormData] = useState<ResourceFormType>({
    name: '',
    code: '',
    category: '',
    type: '',
    description: '',
    prices: [],
    quantities: [],
    totalWeight: '',
    lengthInMm: '',
    unitQuantity: '',
    minQuantity: '',
    maxQuantity: '',
    stockUnit: 'KG',
    dimension1: '',
    dimension2: '',
    markup: '',
    carrierCode: '',
  })

  useEffect(() => {
    let isMounted = true
    const fetchData = async () => {
      const loadId = sourceId || id
      if (loadId) {
        setIsLoadingData(true)
      }
      try {
        const unitsResponse = await ApiService.resources.getUnits()
        const unitsData: Array<{ id: number, name: string }> = Array.isArray(unitsResponse?.data) ? unitsResponse.data : []
        if (isMounted) {
          setAvailableUnitsList(unitsData)
        }

        const findUnitId = (names: string[], fallbackId: number): number => {
          const found = unitsData.find((u: any) => names.includes(String(u.name || '').toUpperCase().trim()))
          return found ? Number(found.id) : fallbackId
        }

        const kgId = findUnitId(['KG', 'QUILOGRAMA', 'KILO', 'QUILO'], 1)
        const mmId = findUnitId(['MM', 'MILIMETRO', 'MILÍMETRO'], 2)
        const unId = findUnitId(['UN', 'UNIDADE', 'PC', 'PÇ', 'PECA', 'PEÇA'], 3)
        const m2Id = findUnitId(['M²', 'M2', 'METRO QUADRADO'], 4)
        const mLinearId = findUnitId(['M LINEAR', 'LINEAR', 'METRO LINEAR', 'M'], 5)
        const percentId = findUnitId(['%', 'PORCENTAGEM', 'PERCENT', 'PERCENTUAL'], 6)
        const litersId = findUnitId(['LITROS', 'LITRO', 'L', 'LT', 'LTS'], 7)

        if (loadId) {
          const response = await ApiService.resources.findById(Number(loadId))
          const data = response.data

          const { suppliers, category, type, prices, quantities, code, description, companyResource, ...rest } = data

          const initialQuantities: QuantityItem[] = []
          if (Array.isArray(quantities) && quantities.length > 0) {
            quantities.forEach((q: any) => {
              if (q.quantity != null && Number(q.quantity) > 0) {
                initialQuantities.push({
                  quantity: Number(q.quantity),
                  unitOfMeasurementId: Number(q.unitOfMeasurementId),
                  minQuantity: q.minQuantity != null ? Number(q.minQuantity) : (rest.minQuantity != null ? Number(rest.minQuantity) : null),
                  maxQuantity: q.maxQuantity != null ? Number(q.maxQuantity) : (rest.maxQuantity != null ? Number(rest.maxQuantity) : null),
                })
              }
            })
          } else {
            const minQ = rest.minQuantity != null ? Number(rest.minQuantity) : null
            const maxQ = rest.maxQuantity != null ? Number(rest.maxQuantity) : null
            const sUnit = String(rest.stockUnit || '').toUpperCase().trim()

            const isLiters = ['LITROS', 'LITRO', 'L', 'LT', 'LTS'].includes(sUnit)
            const isPercent = ['%', 'PORCENTAGEM', 'PERCENT', 'PERCENTUAL'].includes(sUnit)
            const isM2 = ['M²', 'M2', 'METRO QUADRADO'].includes(sUnit)
            const isMLinear = ['M LINEAR', 'LINEAR', 'METRO LINEAR', 'M'].includes(sUnit)

            if (isLiters) {
              if (rest.totalWeight && Number(rest.totalWeight) > 0) {
                initialQuantities.push({ quantity: Number(rest.totalWeight), unitOfMeasurementId: litersId, minQuantity: minQ, maxQuantity: maxQ })
              }
            } else if (isPercent) {
              if (rest.totalWeight && Number(rest.totalWeight) > 0) {
                initialQuantities.push({ quantity: Number(rest.totalWeight), unitOfMeasurementId: percentId, minQuantity: minQ, maxQuantity: maxQ })
              }
            } else if (isM2) {
              if (rest.totalWeight && Number(rest.totalWeight) > 0) {
                initialQuantities.push({ quantity: Number(rest.totalWeight), unitOfMeasurementId: m2Id, minQuantity: minQ, maxQuantity: maxQ })
              }
            } else {
              if (rest.totalWeight && Number(rest.totalWeight) > 0) {
                initialQuantities.push({ quantity: Number(rest.totalWeight), unitOfMeasurementId: kgId, minQuantity: minQ, maxQuantity: maxQ })
              }
            }

            if (isMLinear) {
              if (rest.lengthInMm && Number(rest.lengthInMm) > 0) {
                initialQuantities.push({ quantity: Number(rest.lengthInMm), unitOfMeasurementId: mLinearId, minQuantity: minQ, maxQuantity: maxQ })
              }
            } else {
              if (rest.lengthInMm && Number(rest.lengthInMm) > 0) {
                initialQuantities.push({ quantity: Number(rest.lengthInMm), unitOfMeasurementId: mmId, minQuantity: minQ, maxQuantity: maxQ })
              }
            }

            if (rest.unitQuantity && Number(rest.unitQuantity) > 0) {
              initialQuantities.push({ quantity: Number(rest.unitQuantity), unitOfMeasurementId: unId, minQuantity: minQ, maxQuantity: maxQ })
            }
          }

          const mappedPrices = (prices || []).map((p: any) => ({
            price: Number(p.price) || 0,
            unitOfMeasurementId: Number(p.unitOfMeasurementId),
          }))

          if (isMounted) {
            setFormData({
              ...rest,
              code: variation ? '' : code || '',
              description: description || '',
              prices: mappedPrices,
              quantities: initialQuantities,
              totalWeight: rest.totalWeight != null ? String(rest.totalWeight) : '',
              lengthInMm: rest.lengthInMm != null ? String(rest.lengthInMm) : '',
              unitQuantity: rest.unitQuantity != null ? String(rest.unitQuantity) : '',
              minQuantity: rest.minQuantity != null ? String(rest.minQuantity) : '',
              maxQuantity: rest.maxQuantity != null ? String(rest.maxQuantity) : '',
              stockUnit: rest.stockUnit || (isFashionTenant ? 'KG' : ''),
              dimension1: rest.dimension1 != null ? String(rest.dimension1) : '',
              dimension2: rest.dimension2 != null ? String(rest.dimension2) : '',
              markup: rest.markup != null ? String(rest.markup) : '',
              carrierCode: rest.carrierCode != null ? String(rest.carrierCode) : '',
            })
            currentPricesRef.current = mappedPrices
            setSelectedSuppliers(suppliers)
            if (category) {
              setSelectedCategories([category])
            }
            if (type) {
              setSelectedTypes([type])
            }
            if (rest.materialId && rest.material) {
              setSelectedMaterial({ id: rest.materialId, name: rest.material })
            }
            if (rest.formatId && rest.format) {
              setSelectedFormat({ id: rest.formatId, name: rest.format })
            }
            if (rest.finishingId && rest.finishing) {
              setSelectedFinishing({ id: rest.finishingId, name: rest.finishing })
            }
            if (rest.ncmId && rest.ncm) {
              setSelectedNcm({ id: rest.ncmId, name: rest.ncm })
            }
            if (rest.shelfId && rest.shelf) {
              setSelectedShelf({ id: rest.shelfId, name: rest.shelf })
            }
            if (rest.stockLocationId && rest.stockLocation) {
              setSelectedStockLocation({ id: rest.stockLocationId, name: rest.stockLocation })
            }
            if (rest.stockStreetId && rest.stockStreet) {
              setSelectedStockStreet({ id: rest.stockStreetId, name: rest.stockStreet })
            }
            if (data.colors && Array.isArray(data.colors) && data.colors.length > 0) {
              const firstColor = data.colors[0]
              const resolvedColorId = firstColor.itemColorId ?? firstColor.id ?? null
              const resolvedColorName = firstColor.name || firstColor?.colorName || ''
              if (resolvedColorId && resolvedColorName) {
                const colorObj: LookupItemType = {
                  id: resolvedColorId,
                  name: resolvedColorName,
                  pantone: firstColor.pantone ?? null,
                }
                setPendingColorPantone(colorObj.pantone ?? null)
                if (variation) {
                  setOriginalColorName(colorObj.name)
                  setSelectedColor(null)
                } else {
                  setSelectedColor(colorObj)
                }
              } else {
                setPendingColorPantone(firstColor.pantone ?? null)
              }
            }
            if (data.externalInvoiceUrl) {
              setExternalInvoiceUrl(data.externalInvoiceUrl)
              const resolvedFileName = String(data.externalInvoiceUrl).split('/').pop() || 'nota-fiscal.pdf'
              setInvoiceFiles([{ name: resolvedFileName } as File])
            }
          }
        }
      } catch (error) {
        console.log('Erro ao carregar dados:', error)
      } finally {
        if (isMounted) {
          setIsLoadingData(false)
        }
      }
    }
    fetchData()
    return () => {
      isMounted = false
    }
  }, [id, sourceId])

  useEffect(() => {
    const currentCategoryId = selectedCategories[0]?.id || null

    if (selectedCategories.length === 0) {
      setSelectedTypes([])
      setPreviousCategoryId(null)
    } else if (currentCategoryId !== previousCategoryId && previousCategoryId !== null) {
      setSelectedTypes([])
      setPreviousCategoryId(currentCategoryId)
    } else if (previousCategoryId === null) {
      setPreviousCategoryId(currentCategoryId)
    }
  }, [selectedCategories, previousCategoryId])

  const handleInputChange = (field: keyof ResourceFormType, value: any) => {
    if (field === 'prices' && Array.isArray(value)) {
      currentPricesRef.current = value
      setFormData(prev => ({ ...prev, prices: value }))
      return
    }

    if (field === 'quantities' && Array.isArray(value)) {
      handleQuantitiesChange(value)
      return
    }

    if (field === 'carrierCode') {
      const numericValue = String(value ?? '').replace(/\D/g, '')
      setFormData(prev => ({ ...prev, carrierCode: numericValue }))
      return
    }

    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleQuantitiesChange = (newQuantities: QuantityItem[], unitsOverride?: Array<{ id: number, name: string }>) => {
    const activeUnits = unitsOverride || availableUnitsList
    setFormData(prev => {
      let totalWeight = '0'
      let lengthInMm = '0'
      let unitQuantity = '0'
      let stockUnit = prev.stockUnit

      if (newQuantities.length > 0) {
        newQuantities.forEach(q => {
          const unitObj = activeUnits.find(u => Number(u.id) === Number(q.unitOfMeasurementId))
          const unitName = (unitObj?.name || '').toUpperCase().trim()

          if (['KG', 'QUILOGRAMA', 'KILO', 'QUILO'].includes(unitName) || (!unitName && q.unitOfMeasurementId === 1)) {
            totalWeight = String(q.quantity)
            stockUnit = 'KG'
          } else if (['MM', 'MILIMETRO', 'MILÍMETRO'].includes(unitName) || (!unitName && q.unitOfMeasurementId === 2)) {
            lengthInMm = String(q.quantity)
            if (!stockUnit || stockUnit === 'KG') stockUnit = 'MM'
          } else if (['UN', 'UNIDADE', 'PC', 'PÇ', 'PECA', 'PEÇA'].includes(unitName) || (!unitName && q.unitOfMeasurementId === 3)) {
            unitQuantity = String(q.quantity)
            if (!stockUnit) stockUnit = 'UN'
          } else if (['LITROS', 'LITRO', 'L', 'LT', 'LTS'].includes(unitName)) {
            totalWeight = String(q.quantity)
            stockUnit = 'Litros'
          } else if (['%', 'PORCENTAGEM', 'PERCENT', 'PERCENTUAL'].includes(unitName)) {
            totalWeight = String(q.quantity)
            stockUnit = '%'
          } else if (['M²', 'M2', 'METRO QUADRADO'].includes(unitName)) {
            totalWeight = String(q.quantity)
            stockUnit = 'M²'
          } else if (['M LINEAR', 'LINEAR', 'METRO LINEAR', 'M'].includes(unitName)) {
            lengthInMm = String(q.quantity)
            stockUnit = 'M linear'
          }
        })
      }

      return {
        ...prev,
        quantities: newQuantities,
        totalWeight,
        lengthInMm,
        unitQuantity,
        stockUnit,
      }
    })
  }

  const handleUploadInvoice = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return
    const file = files[0]
    try {
      const uploaded = await AttachmentApiService.upload(file)
      const fileUrl = uploaded?.fileUrl
      if (fileUrl) {
        setExternalInvoiceUrl(fileUrl)
        setInvoiceFiles([file])
      }
    } catch (error) {
      console.error('Erro ao fazer upload da nota fiscal:', error)
      setFeedback({
        isOpen: true,
        message: 'Erro ao fazer upload do arquivo da nota fiscal.',
        statusOk: false,
      })
    }
    e.target.value = ''
  }

  const handleRemoveInvoice = (_index?: number) => {
    setInvoiceFiles([])
    setExternalInvoiceUrl(null)
  }

  const validateStep1 = () => {
    const missing: string[] = []
    if (!formData.name.trim()) missing.push('Nome')
    if (missing.length > 0) {
      setValidationFields(missing)
      setIsValidationModalOpen(true)
      return false
    }
    return true
  }

  const validateStep2 = () => {
    const missing: string[] = []
    if (isFashionTenant && !selectedColor) missing.push('Cor')
    if (missing.length > 0) {
      setValidationFields(missing)
      setIsValidationModalOpen(true)
      return false
    }
    return true
  }

  const handleNext = () => {
    if (currentStep === 1) {
      if (validateStep1()) setCurrentStep(2)
    } else if (currentStep === 2) {
      if (validateStep2()) setCurrentStep(3)
    }
  }

  const handleBack = () => {
    if (currentStep === 2) setCurrentStep(1)
    else if (currentStep === 3) {
      ncmTexto.limpar()
      setCurrentStep(2)
    }
  }

  const onSubmit = async () => {
    if (ncmTexto.bloquear()) {
      setCurrentStep(3)
      setFeedback({ isOpen: true, message: MENSAGEM_NCM_INEXISTENTE, statusOk: false })
      return
    }
    if (priceManagerRef.current) {
      const priceAdded = priceManagerRef.current.addPendingPrice()
      if (priceAdded) {
        await new Promise(resolve => setTimeout(resolve, 100))
      }
    }
    if (quantityManagerRef.current) {
      quantityManagerRef.current.addPendingQuantity()
      quantityManagerRef.current.savePendingEdit()
    }

    await new Promise(resolve => setTimeout(resolve, 50))

    if (!isFashionTenant && (!formData.code || !formData.code.toString().trim())) {
      setCodeError(true)
      setCurrentStep(3)
      setFeedback({
        isOpen: true,
        message: 'O campo "Código" é obrigatório!',
        statusOk: false,
      })
    }

    try {
      const finalPrices = currentPricesRef.current.length > 0 ? currentPricesRef.current : formData.prices
      const selectedCategory = selectedCategories[0] || null
      const selectedType = selectedTypes[0] || null

      const resolvedMaterialId = selectedMaterial ? selectedMaterial.id : null

      let totalWeight = 0
      let lengthInMm = 0
      let unitQuantity = 0
      let stockUnit = formData.stockUnit || ''

      ;(formData.quantities || []).forEach((q: any) => {
        const unitObj = availableUnitsList.find(u => Number(u.id) === Number(q.unitOfMeasurementId))
        const unitName = (unitObj?.name || '').toUpperCase().trim()

        if (['KG', 'QUILOGRAMA', 'KILO', 'QUILO'].includes(unitName) || (!unitName && q.unitOfMeasurementId === 1)) {
          totalWeight = Number(q.quantity) || 0
          stockUnit = 'KG'
        } else if (['MM', 'MILIMETRO', 'MILÍMETRO'].includes(unitName) || (!unitName && q.unitOfMeasurementId === 2)) {
          lengthInMm = Number(q.quantity) || 0
          if (!stockUnit || stockUnit === 'KG') stockUnit = 'MM'
        } else if (['UN', 'UNIDADE', 'PC', 'PÇ', 'PECA', 'PEÇA'].includes(unitName) || (!unitName && q.unitOfMeasurementId === 3)) {
          unitQuantity = Number(q.quantity) || 0
          if (!stockUnit) stockUnit = 'UN'
        } else if (['LITROS', 'LITRO', 'L', 'LT', 'LTS'].includes(unitName)) {
          totalWeight = Number(q.quantity) || 0
          stockUnit = 'Litros'
        } else if (['%', 'PORCENTAGEM', 'PERCENT', 'PERCENTUAL'].includes(unitName)) {
          totalWeight = Number(q.quantity) || 0
          stockUnit = '%'
        } else if (['M²', 'M2', 'METRO QUADRADO'].includes(unitName)) {
          totalWeight = Number(q.quantity) || 0
          stockUnit = 'M²'
        } else if (['M LINEAR', 'LINEAR', 'METRO LINEAR', 'M'].includes(unitName)) {
          lengthInMm = Number(q.quantity) || 0
          stockUnit = 'M linear'
        }
      })

      if (!stockUnit) {
        stockUnit = formData.stockUnit || (isFashionTenant ? 'UN' : 'KG')
      }

      const requestData = {
        name: formData.name,
        description: formData.description || '',
        code: formData.code ? (/^[\d\s]+$/.test(String(formData.code).trim()) ? String(formData.code).replace(/\s+/g, '') : String(formData.code).trim()) : null,
        dimension1: formData.dimension1 !== '' && formData.dimension1 != null ? Number(String(formData.dimension1).replace(',', '.')) : null,
        dimension2: formData.dimension2 !== '' && formData.dimension2 != null ? Number(String(formData.dimension2).replace(',', '.')) : null,
        minQuantity: formData.minQuantity !== '' && formData.minQuantity != null ? Number(String(formData.minQuantity).replace(',', '.')) : null,
        maxQuantity: formData.maxQuantity !== '' && formData.maxQuantity != null ? Number(String(formData.maxQuantity).replace(',', '.')) : null,
        markup: formData.markup !== '' && formData.markup != null ? Number(String(formData.markup).replace(',', '.')) : null,
        totalWeight,
        lengthInMm,
        unitQuantity,
        stockUnit,
        prices: finalPrices.map((price: any) => ({
          price: Number(price.price),
          unitOfMeasurementId: Number(price.unitOfMeasurementId),
        })),
        quantities: (formData.quantities || []).map((q: any) => ({
          quantity: Number(q.quantity),
          unitOfMeasurementId: Number(q.unitOfMeasurementId),
          minQuantity: q.minQuantity != null ? Number(q.minQuantity) : null,
          maxQuantity: q.maxQuantity != null ? Number(q.maxQuantity) : null,
        })),
        carrierCode: formData.carrierCode ? Number(formData.carrierCode) : null,
        suppliers: selectedSuppliers.map(supplier => supplier.id),
        categoryId: selectedCategory ? selectedCategory.id : null,
        category: selectedCategory ? selectedCategory.name : null,
        typeId: selectedType ? selectedType.id : null,
        type: selectedType ? selectedType.name : null,
        materialId: resolvedMaterialId,
        formatId: selectedFormat ? selectedFormat.id : null,
        finishingId: selectedFinishing ? selectedFinishing.id : null,
        ncmId: selectedNcm ? selectedNcm.id : null,
        shelfId: selectedShelf ? selectedShelf.id : null,
        stockLocationId: selectedStockLocation ? selectedStockLocation.id : null,
        stockStreetId: selectedStockStreet ? selectedStockStreet.id : null,
        colors: selectedColor
          ? [{
            colorName: selectedColor.name,
            pantone: (selectedColor.pantone ?? pendingColorPantone)
              ? {
                code: (selectedColor.pantone ?? pendingColorPantone)!.code,
                name: (selectedColor.pantone ?? pendingColorPantone)!.name,
              }
              : undefined,
          }]
          : [],
        externalInvoiceUrl: externalInvoiceUrl || null,
      }

      const isEdit = !!id && !variation
      const action = isEdit ? 'atualizado' : variation ? 'criado como variação' : 'criado'

      let response: any = null
      if (isEdit) {
        response = await ApiService.resources.update(Number(id), requestData)
      } else {
        response = await ApiService.resources.create(requestData)
      }

      const wasCodeEmpty = !requestData.code || !requestData.code.trim()
      const finalCode = response?.data?.code || response?.data?.Code?.code

      if (!isEdit && wasCodeEmpty && isFashionTenant && finalCode) {
        setFeedback({
          message: `Código não informado, criado automaticamente com: ${finalCode}`,
          isOpen: true,
          statusOk: true,
          timeout: 20000,
        })
      } else {
        setFeedback({ message: `Insumo ${action} com sucesso!`, isOpen: true, statusOk: true })
      }
      if (setStockContextRefresh) {
        setStockContextRefresh((prev: boolean) => !prev)
      }
      navigate(redirectPath)
    } catch (error: any) {
      const action = id && !variation ? 'atualizar' : 'criar'
      let errorMessage = `Erro ao ${action} insumo`
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.response?.status === 400) {
        errorMessage = 'Dados inválidos. Verifique as informações inseridas.'
      } else if (error.response?.status === 500) {
        errorMessage = 'Erro interno do servidor. Tente novamente.'
      } else if (error.message) {
        errorMessage = error.message
      }
      setFeedback({ message: errorMessage, isOpen: true, statusOk: false })
    }
  }

  const itemName = isFashionTenant ? 'Aviamento' : 'Insumo'


  const title = variation ? 'Adicionar Variação' : id ? `Editar ${itemName.toLowerCase()}` : `Novo ${itemName}`

  return (
    <ModalTest>
      <ConfirmationModal
        isOpen={isValidationModalOpen}
        title="Campos obrigatórios não preenchidos"
        message={
          <div>
            Preencha os seguintes campos antes de continuar:
            <ul style={{ marginTop: 8, paddingLeft: 20 }}>
              {validationFields.map((field, i) => (
                <li key={i}>{field}</li>
              ))}
            </ul>
          </div>
        }
        actions={[{ label: 'Entendi', onClick: () => setIsValidationModalOpen(false), variant: 'primary' }]}
      />

      <ContainerOfModal width='60vw' maxHeight='90vh'>
        <Header style={{ marginBottom: '0.5rem' }}>
          <HeaderTitle>{title}</HeaderTitle>
          <CloseDiv onClick={() => navigate(redirectPath)}>
            <X size={20} />
          </CloseDiv>
        </Header>

        <ProgressBarDiv>
          <ProgressBarCircle $active={true} />
          <ProgressBarLine $active={currentStep >= 2} />
          <ProgressBarCircle $active={currentStep >= 2} />
          <ProgressBarLine $active={currentStep >= 3} />
          <ProgressBarCircle $active={currentStep >= 3} />
        </ProgressBarDiv>

        {isLoadingData ? (
          <LoadingContainer>
            <LoadingSpinner size={36} />
            <LoadingText>Carregando dados...</LoadingText>
          </LoadingContainer>
        ) : (
          <>
            {currentStep === 1 && (
          <StepGrid>
            <InputDiv>
              <Label style={nameError ? { color: 'red' } : {}}>
                Nome
                <span style={{ color: 'red', marginLeft: 2 }}>*</span>
                {nameError && (
                  <span style={{ fontSize: '12px', color: 'red', marginLeft: 6, fontWeight: 400 }}>
                    O nome é obrigatório!
                  </span>
                )}
              </Label>
              <Input
                autoComplete='off'
                type='text'
                value={formData.name}
                onChange={e => { handleInputChange('name', e.target.value); setNameError(false) }}
                placeholder="Digite o nome do insumo"
                style={{ height: '40px', ...(nameError ? { border: '1px solid red', borderRadius: '2rem' } : {}) }}
              />
            </InputDiv>

            <InputDiv>
              <Label>
                Categoria
              </Label>
              <InfiniteSelect
                mode="crud"
                value={selectedCategories[0] || null}
                onSelect={option => {
                  setSelectedCategories(option ? [option] : [])
                  setSelectedTypes([])
                  setCategoryError(false)
                }}
                endpointConfig={{
                  listPath: '/resources/resource/util/categories',
                  createPath: '/resources/resource/util/categories',
                  deletePath: '/resources/resource/util/categories',
                  headers: { 'Cache-Control': 'no-cache' },
                }}
                onItemCreate={async (name: string) => {
                  const response = await ApiService.resources.createCategory({ name })
                  return response.data
                }}
                onItemDelete={async (catId: any) => {
                  await ApiService.resources.deleteCategory(catId)
                }}
                fetchFunction={async (_limit, _offset, searchVal) => {
                  const response = await ApiService.resources.getCategories({
                    query: searchVal,
                    headers: { 'Cache-Control': 'no-cache' },
                  })
                  const items = Array.isArray(response?.data) ? response.data : []
                  return searchVal
                    ? items.filter((c: any) => c.name?.toLowerCase().includes(searchVal.toLowerCase()))
                    : items
                }}
                placeholder="Buscar ou criar categoria"
                initialOptions={selectedCategories}
                allowCreate={true}
                allowDelete={true}
                showCreateOnlyIfNotExists={true}
                minCreateLength={2}
              />
            </InputDiv>

            <InputDiv>
              <Label>Tipo</Label>
              <InfiniteSelect
                key={`resource-type-${selectedCategories[0]?.id || 'none'}`}
                mode="crud"
                value={selectedTypes[0] || null}
                onSelect={option => setSelectedTypes(option ? [option] : [])}
                categoryId={selectedCategories[0]?.id}
                endpointConfig={{
                  listPath: '/resources/resource/util/types',
                  createPath: '/resources/resource/util/types',
                  deletePath: '/resources/resource/util/types',
                  additionalParams: selectedCategories[0]?.id ? { categoryId: selectedCategories[0]?.id } : undefined,
                  headers: { 'Cache-Control': 'no-cache' },
                }}
                onItemCreate={async (name: string) => {
                  if (!selectedCategories[0]?.id) {
                    throw new Error('Selecione uma categoria primeiro')
                  }
                  const response = await ApiService.resources.createType({ name, categoryId: selectedCategories[0]?.id })
                  return response.data
                }}
                onItemDelete={async (typeId: any) => {
                  await ApiService.resources.deleteType(typeId)
                }}
                fetchFunction={async (_limit, _offset, searchVal) => {
                  if (!selectedCategories[0]?.id) return []
                  const response = await ApiService.resources.getTypes({
                    categoryId: selectedCategories[0]?.id,
                    query: searchVal,
                  })
                  const items = Array.isArray(response?.data) ? response.data : []
                  const mapped = items.map((t: any) => ({ id: t.id, name: t.name }))
                  return searchVal
                    ? mapped.filter((t: any) => t.name?.toLowerCase().includes(searchVal.toLowerCase()))
                    : mapped
                }}
                placeholder={selectedCategories[0]?.id ? "Buscar ou criar tipo" : "Selecione uma categoria primeiro"}
                disabled={!selectedCategories[0]?.id}
                initialOptions={selectedTypes}
                allowCreate={Boolean(selectedCategories[0]?.id)}
                allowDelete={Boolean(selectedCategories[0]?.id)}
                showCreateOnlyIfNotExists={true}
                minCreateLength={2}
              />
            </InputDiv>

            <InputDiv>
              <Label style={materialError ? { color: 'red' } : {}}>
                Material
                <span style={{ color: 'red', marginLeft: 2 }}>*</span>
                {materialError && (
                  <span style={{ fontSize: '12px', color: 'red', marginLeft: 6, fontWeight: 400 }}>
                    O material é obrigatório!
                  </span>
                )}
              </Label>
              <div style={materialError ? { border: '1px solid red', borderRadius: '2rem', overflow: 'hidden' } : {}}>
                <InfiniteSelect
                  mode="crud"
                  value={selectedMaterial}
                  onSelect={option => { setSelectedMaterial(option); setMaterialError(false) }}
                  fetchFunction={async (limit, offset, search?) => {
                    const response = await ApiService.stockLookup.getMaterialsForResource()
                    const items = response.data || []
                    const filtered = search
                      ? items.filter((m: any) => m.name?.toLowerCase().includes(search.toLowerCase()))
                      : items
                    return filtered.map((m: any) => ({ id: m.id, name: m.name }))
                  }}
                  onItemCreate={async (name: string) => {
                    const response = await ApiService.stockLookup.createMaterial({ name: name.trim(), type: 'RESOURCE' })
                    return response.data
                  }}
                  onItemDelete={async (matId: any) => {
                    await ApiService.stockLookup.deleteMaterial(matId)
                  }}
                  placeholder="Buscar ou criar material"
                  allowCreate={true}
                  allowDelete={true}
                  showCreateOnlyIfNotExists={true}
                  minCreateLength={2}
                />
              </div>
            </InputDiv>

            <InputDiv>
              <Label>Formato</Label>
              <InfiniteSelect
                mode="crud"
                value={selectedFormat}
                onSelect={option => setSelectedFormat(option)}
                fetchFunction={async (limit, offset, search?) => {
                  const response = await ApiService.stockLookup.getFormats()
                  const items = response.data || []
                  const filtered = search
                    ? items.filter((m: any) => m.name?.toLowerCase().includes(search.toLowerCase()))
                    : items
                  return filtered.map((m: any) => ({ id: m.id, name: m.name }))
                }}
                onItemCreate={async (name: string) => {
                  const response = await ApiService.stockLookup.createFormat({ name })
                  return response.data
                }}
                onItemDelete={async (fmtId: any) => {
                  await ApiService.stockLookup.deleteFormat(fmtId)
                }}
                placeholder="Buscar ou criar formato"
                allowCreate={true}
                allowDelete={true}
                showCreateOnlyIfNotExists={true}
                minCreateLength={2}
              />
            </InputDiv>

            <InputDiv>
              <Label>Acabamento</Label>
              <InfiniteSelect
                mode="crud"
                value={selectedFinishing}
                onSelect={option => setSelectedFinishing(option)}
                fetchFunction={async (limit, offset, search?) => {
                  const response = await ApiService.stockLookup.getFinishings()
                  const items = response.data || []
                  const filtered = search
                    ? items.filter((m: any) => m.name?.toLowerCase().includes(search.toLowerCase()))
                    : items
                  return filtered.map((m: any) => ({ id: m.id, name: m.name || m.type }))
                }}
                onItemCreate={async (name: string) => {
                  const response = await ApiService.stockLookup.createFinishing({ name: name.trim() })
                  return response.data
                }}
                onItemDelete={async (finId: any) => {
                  await ApiService.stockLookup.deleteFinishing(finId)
                }}
                placeholder="Buscar ou criar acabamento"
                allowCreate={true}
                allowDelete={true}
                showCreateOnlyIfNotExists={true}
                minCreateLength={2}
              />
            </InputDiv>

            <SelectSupplierInResource suppliers={selectedSuppliers} setSuppliers={setSelectedSuppliers} />

            <InputDiv>
              <Label>Rua do Estoque</Label>
              <InfiniteSelect
                mode="crud"
                value={selectedStockStreet}
                onSelect={option => setSelectedStockStreet(option)}
                fetchFunction={async (limit, offset, search?) => {
                  const response = await ApiService.stockLookup.getStockStreets()
                  const items = response.data || []
                  const filtered = search
                    ? items.filter((m: any) => m.name?.toLowerCase().includes(search.toLowerCase()))
                    : items
                  return filtered.map((m: any) => ({ id: m.id, name: m.name }))
                }}
                onItemCreate={async (name: string) => {
                  const response = await ApiService.stockLookup.createStockStreet({ name })
                  return response.data
                }}
                onItemDelete={async (streetId: any) => {
                  await ApiService.stockLookup.deleteStockStreet(streetId)
                }}
                placeholder="Buscar ou criar rua"
                allowCreate={true}
                allowDelete={true}
                showCreateOnlyIfNotExists={true}
                minCreateLength={2}
              />
            </InputDiv>

            <InputDiv>
              <Label>Prateleira</Label>
              <InfiniteSelect
                mode="crud"
                value={selectedShelf}
                onSelect={option => setSelectedShelf(option)}
                fetchFunction={async (limit, offset, search?) => {
                  const response = await ApiService.stockLookup.getShelfs()
                  const items = response.data || []
                  const filtered = search
                    ? items.filter((m: any) => m.name?.toLowerCase().includes(search.toLowerCase()))
                    : items
                  return filtered.map((m: any) => ({ id: m.id, name: m.name }))
                }}
                onItemCreate={async (name: string) => {
                  const response = await ApiService.stockLookup.createShelf({ name })
                  return response.data
                }}
                onItemDelete={async (shelfId: any) => {
                  await ApiService.stockLookup.deleteShelf(shelfId)
                }}
                placeholder="Buscar ou criar prateleira"
                allowCreate={true}
                allowDelete={true}
                showCreateOnlyIfNotExists={true}
                minCreateLength={2}
              />
            </InputDiv>

            <InputDiv>
              <Label>Localização</Label>
              <InfiniteSelect
                mode="crud"
                value={selectedStockLocation}
                onSelect={option => setSelectedStockLocation(option)}
                fetchFunction={async (limit, offset, search?) => {
                  const response = await ApiService.stockLookup.getStockLocations()
                  const items = response.data || []
                  const filtered = search
                    ? items.filter((m: any) => m.name?.toLowerCase().includes(search.toLowerCase()))
                    : items
                  return filtered.map((m: any) => ({ id: m.id, name: m.name }))
                }}
                onItemCreate={async (name: string) => {
                  const response = await ApiService.stockLookup.createStockLocation({ name })
                  return response.data
                }}
                onItemDelete={async (locId: any) => {
                  await ApiService.stockLookup.deleteStockLocation(locId)
                }}
                placeholder="Buscar ou criar localização"
                allowCreate={true}
                allowDelete={true}
                showCreateOnlyIfNotExists={true}
                minCreateLength={2}
              />
            </InputDiv>

            <InputDiv>
              <Label>Código da Transportadora</Label>
              <Input
                autoComplete='off'
                type='text'
                inputMode='numeric'
                pattern='[0-9]*'
                value={formData.carrierCode || ''}
                onChange={e => handleInputChange('carrierCode', e.target.value)}
                onKeyDown={(e) => {
                  if (
                    !/[0-9]/.test(e.key) &&
                    !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'].includes(e.key) &&
                    !e.ctrlKey &&
                    !e.metaKey
                  ) {
                    e.preventDefault()
                  }
                }}
                placeholder="Digite o código"
                style={{ height: '40px' }}
              />
            </InputDiv>

            <InputDiv style={{ gridColumn: '1 / -1' }}>
              <Label>Descrição</Label>
              <Input
                autoComplete='off'
                type='text'
                value={formData.description}
                onChange={e => handleInputChange('description', e.target.value)}
                placeholder="Digite a descrição do insumo"
                style={{ height: '40px' }}
              />
            </InputDiv>

            <ButtonContainer>
              <Button type='button' onClick={() => navigate(redirectPath)} className='cancel'>
                Cancelar
              </Button>
              <Button
                type='button'
                onClick={handleNext}
              >
                Próximo <ChevronRight size={16} />
              </Button>
            </ButtonContainer>
          </StepGrid>
        )}

        {currentStep === 2 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', width: '100%' }}>
            <div style={{ display: 'flex', gap: '2.5rem', width: '100%' }}>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <InputDiv>
                    <Label>Dimensão 1</Label>
                    <Input
                      placeholder='Digite aqui'
                      name='dimension1'
                      value={formData.dimension1 || ''}
                      onChange={e => handleInputChange('dimension1', e.target.value)}
                      autoComplete='off'
                      style={{ height: '40px' }}
                    />
                  </InputDiv>

                  <InputDiv>
                    <Label>Dimensão 2</Label>
                    <Input
                      placeholder='Digite aqui'
                      name='dimension2'
                      value={formData.dimension2 || ''}
                      onChange={e => handleInputChange('dimension2', e.target.value)}
                      autoComplete='off'
                      style={{ height: '40px' }}
                    />
                  </InputDiv>
                </div>

                <QuantityManager
                  ref={quantityManagerRef}
                  quantities={formData.quantities || []}
                  onChange={quantities => handleQuantitiesChange(quantities)}
                  baseUrl={`${process.env.REACT_APP_GATEWAY_BASE_URL}/resources` || ''}
                  label="Quantidades"
                  isFashion={true}
                />

                {isFashionTenant && (
                  <ColorField
                    isFashionTenant={isFashionTenant}
                    colorError={colorError}
                    pantoneError={pantoneError}
                    originalColorName={originalColorName}
                    selectedColor={selectedColor}
                    pendingColorPantone={pendingColorPantone}
                    setSelectedColor={setSelectedColor}
                    setPendingColorPantone={setPendingColorPantone}
                    setColorError={setColorError}
                    setPantoneError={setPantoneError}
                  />
                )}
              </div>

              <div style={{ flex: 1, borderLeft: '1px solid #eee', paddingLeft: '2.5rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <PriceManager
                  ref={priceManagerRef}
                  prices={formData.prices || []}
                  onChange={prices => handleInputChange('prices', prices)}
                  baseUrl={`${process.env.REACT_APP_GATEWAY_BASE_URL}/resources` || ''}
                  label="Preços"
                  allowedUnits={['kg', 'mm', 'un', 'm²', 'm linear', 'm2', 'm', 'linear', 'metro quadrado', 'metro linear', '%', 'porcentagem', 'litro', 'litros', 'l']}
                  defaultUnitName="UN"
                />

                {!isFashionTenant && (
                  <InputDiv>
                    <Label>Markup</Label>
                    <Input
                      placeholder='Ex.: 1,5'
                      name='markup'
                      value={formData.markup || ''}
                      onChange={e => handleInputChange('markup', e.target.value)}
                      autoComplete='off'
                      style={{ height: '40px' }}
                    />
                  </InputDiv>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <InputDiv>
                    <Label>Estoque Mínimo</Label>
                    <Input
                      type="number"
                      placeholder='Ex.: 5'
                      name='minQuantity'
                      value={formData.minQuantity ?? ''}
                      onChange={e => handleInputChange('minQuantity', e.target.value)}
                      autoComplete='off'
                      style={{ height: '40px' }}
                    />
                  </InputDiv>

                  <InputDiv>
                    <Label>Estoque Máximo</Label>
                    <Input
                      type="number"
                      placeholder='Ex.: 100'
                      name='maxQuantity'
                      value={formData.maxQuantity ?? ''}
                      onChange={e => handleInputChange('maxQuantity', e.target.value)}
                      autoComplete='off'
                      style={{ height: '40px' }}
                    />
                  </InputDiv>
                </div>
              </div>
            </div>

            <ButtonContainer>
              <Button type='button' onClick={handleBack} className='cancel'>
                <ChevronLeft size={16} /> Voltar
              </Button>
              <Button type='button' onClick={handleNext}>
                Próximo <ChevronRight size={16} />
              </Button>
            </ButtonContainer>
          </div>
        )}

        {currentStep === 3 && (
          <StepGrid>
            <InputDiv>
              <Label style={codeError ? { color: 'red' } : {}}>
                Código
                {!isFashionTenant && <span style={{ color: 'red', marginLeft: 2 }}>*</span>}
                {codeError && (
                  <span style={{ fontSize: '12px', color: 'red', marginLeft: 6, fontWeight: 400 }}>
                    O código é obrigatório!
                  </span>
                )}
              </Label>
              <div style={codeError ? { border: '1px solid red', borderRadius: '2rem', overflow: 'hidden' } : {}}>
                <InfiniteSelect
                  mode="crud"
                  allowFreeText={true}
                  value={formData.code ? { id: formData.code, name: formData.code } : null}
                  onSelect={option => {
                    handleInputChange('code', option ? option.name : '')
                    setCodeError(false)
                  }}
                  fetchFunction={async (limit, offset, search?) => {
                    const response = await ApiService.get('/backend/code', { type: 'resource' })
                    const items = Array.isArray(response?.data) ? response.data : []
                    const mapped = items.map((c: any) => {
                      if (typeof c === 'string') return { id: c, name: c }
                      return { id: c.id || c.code, name: c.code || '' }
                    })
                    return search
                      ? mapped.filter((item: any) => item.name?.toLowerCase().includes(search.toLowerCase()))
                      : mapped
                  }}
                  onItemCreate={async (name: string) => {
                    const trimmed = name.trim()
                    handleInputChange('code', trimmed)
                    setCodeError(false)
                    return { id: trimmed, name: trimmed } as any
                  }}
                  onItemDelete={async (code: any) => {
                    await ApiService.code.delete(code)
                  }}
                  placeholder="Buscar ou criar Código"
                  allowCreate={true}
                  allowDelete={true}
                  showCreateOnlyIfNotExists={true}
                  minCreateLength={2}
                />
              </div>
            </InputDiv>

            <InputDiv>
              <Label>NCM</Label>
              <div onInput={ncmTexto.aoDigitar} onBlur={ncmTexto.aoSair}>
              <InfiniteSelect
                mode="crud"
                value={selectedNcm}
                onSelect={(option: any) => {
                  ncmTexto.limpar()
                  if (!option) {
                    setSelectedNcm(null)
                    return
                  }
                  setSelectedNcm({ id: option.id, name: option.descricao ? `${option.ncm} · ${option.descricao}` : option.ncm })
                }}
                endpointConfig={{
                  listPath: '/backend/ncm/catalogo',
                  searchParam: 'q',
                  transformResponse: (data: any) => (Array.isArray(data) ? data : []).map((item: any) => ({
                    id: item.id,
                    name: `${item.ncm}${item.descricao ? ` · ${item.descricao}` : ''}${item.jaCadastrado ? ' · em uso' : ''}`,
                    ncm: item.ncm,
                    descricao: item.descricao,
                    jaCadastrado: item.jaCadastrado,
                  })),
                }}
                placeholder="Buscar NCM"
                emptyOptionsMessage="Nenhum item disponível"
                errorOptionsMessage={MENSAGEM_NCM_INEXISTENTE}
                allowCreate={false}
                allowDelete={false}
              />
              <NcmErroCampo visivel={ncmTexto.exibirErro} />
              </div>
            </InputDiv>

            <InputDiv style={{ gridColumn: '1 / -1' }}>
              <FileBlock>
                <FileBlockDiv>
                  <FileHeaderText>Nota fiscal</FileHeaderText>
                  {invoiceFiles.length === 0 ? (
                    <FileInputDiv>
                      <FileInputLabel
                        htmlFor={undefined}
                        onClick={(e) => {
                          e.preventDefault()
                          setIsSourceChoiceOpen(true)
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        Procurar
                      </FileInputLabel>
                      <FileInputHidden
                        ref={invoiceInputRef}
                        id='invoice-upload'
                        type='file'
                        accept='application/pdf,image/*'
                        onClick={(e) => e.stopPropagation()}
                        onChange={handleUploadInvoice}
                      />
                      <FileFirstText>ou arraste um arquivo até aqui</FileFirstText>
                      <FileSecondText>Apenas arquivos .pdf de no máximo 10mb</FileSecondText>
                    </FileInputDiv>
                  ) : (
                    <>
                      <FilesInfoDiv>
                        {invoiceFiles.map((file, index) => (
                          <FileInfoDiv key={index}>
                            <FileInfoImg src={fileIcon} alt='arquivo' />
                            <FileMiddleDiv>
                              <FileUpperMiddleDiv>
                                <FileName
                                  style={{ cursor: externalInvoiceUrl ? 'pointer' : 'default', textDecoration: externalInvoiceUrl ? 'underline' : 'none' }}
                                  onClick={() => {
                                    if (externalInvoiceUrl) openSignedAttachment(externalInvoiceUrl)
                                  }}
                                >
                                  {file.name}
                                </FileName>
                              </FileUpperMiddleDiv>
                              <FileProgressContainer>
                                <FileProgressFill />
                              </FileProgressContainer>
                            </FileMiddleDiv>
                            <FileInfoCancelImg
                              src={cancelIcon}
                              alt='remover'
                              onClick={() => handleRemoveInvoice(index)}
                            />
                          </FileInfoDiv>
                        ))}
                      </FilesInfoDiv>
                      <FileInputLabel
                        htmlFor={undefined}
                        onClick={(e) => {
                          e.preventDefault()
                          setIsSourceChoiceOpen(true)
                        }}
                        className='down'
                        style={{ cursor: 'pointer' }}
                      >
                        Procurar
                      </FileInputLabel>
                      <FileInputHidden
                        ref={invoiceInputRef}
                        type='file'
                        accept='application/pdf,image/*'
                        onClick={(e) => e.stopPropagation()}
                        onChange={handleUploadInvoice}
                      />
                    </>
                  )}
                </FileBlockDiv>
              </FileBlock>
            </InputDiv>

            <ButtonContainer>
              <Button type='button' onClick={handleBack} className='cancel'>
                <ChevronLeft size={16} /> Voltar
              </Button>
              <Button type='button' onClick={onSubmit}>
                {variation ? '+ Criar Variação' : id ? 'Alterar' : `+ Adicionar ${itemName}`}
              </Button>
            </ButtonContainer>
          </StepGrid>
        )}
          </>
        )}

        <AttachmentSourceModal
          isOpen={isSourceChoiceOpen}
          onClose={() => setIsSourceChoiceOpen(false)}
          title='Adicionar Nota Fiscal'
          description='Escolha como deseja adicionar o arquivo da nota fiscal:'
          onChooseUpload={() => {
            if (invoiceInputRef.current) {
              invoiceInputRef.current.click()
            } else {
              document.getElementById('invoice-upload')?.click()
            }
          }}
          onChooseGallery={() => {
            setIsGalleryOpen(true)
          }}
        />

        <AttachmentGalleryModal
          isOpen={isGalleryOpen}
          onClose={() => setIsGalleryOpen(false)}
          multiple={false}
          defaultFilter='all'
          title='Galeria - Selecionar Nota Fiscal'
          onSelect={handleSelectInvoiceFromGallery}
        />
      </ContainerOfModal>
    </ModalTest>
  )
}

export default CreateResourceModal
